import logo from "@/assets/logo.png";
import { ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "June 13, 2026";

function Section({ n, title, children }) {
  return (
    <section className="mb-8" data-testid={`terms-section-${n}`}>
      <h2 className="font-display text-xl font-bold mb-2">{n}. {title}</h2>
      <div className="space-y-2 text-sm text-black/70 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-card/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <img src={logo} alt="Short Stack Hawai'i" className="h-9 w-auto" />
          <a href="/" data-testid="terms-back-link" className="press inline-flex items-center gap-1.5 text-sm font-bold px-4 h-9 rounded-full border-2 border-black bg-white">
            <ArrowLeft size={15} weight="bold" /> Back
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="font-display text-3xl font-black tracking-tight mb-1">Terms &amp; Conditions</h1>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-8">Effective Date: {EFFECTIVE_DATE}</p>

        <Section n={1} title="Agreement to Terms">
          <p>These Terms &amp; Conditions (“Terms”) govern your access to and use of the Short Stack Hawaii LLC platform and services (“Service”). By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.</p>
        </Section>

        <Section n={2} title="Eligibility">
          <p>The Service is intended for businesses and users who are at least 18 years old, or the age of majority in your jurisdiction. By using the Service, you represent that you meet this requirement and that you are authorized to act on behalf of the business you register.</p>
        </Section>

        <Section n={3} title="Accounts & Access">
          <p>You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. Access to features is governed by role-based permissions assigned within your organization. You agree to provide accurate information and to keep it up to date.</p>
        </Section>

        <Section n={4} title="Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Use the Service for any unlawful, harmful, or fraudulent purpose.</li>
            <li>Attempt to gain unauthorized access to any part of the Service or other users' data.</li>
            <li>Interfere with, disrupt, or overload the Service or its infrastructure.</li>
            <li>Upload malicious code or violate the rights of others.</li>
          </ul>
        </Section>

        <Section n={5} title="SMS Messaging & Mobile Data">
          <p><strong>Program name:</strong> Short Stack Crew SMS Notifications. <strong>Business:</strong> Short Stack Hawaii LLC.</p>
          <p><strong>Message types:</strong> published work schedules, shift changes, PTO request updates, task assignments, meeting reminders, account security notices, company announcements, and important operational alerts. SMS is optional and is not a condition of employment or app use.</p>
          <p>Message frequency varies based on employee activity. Message and data rates may apply.</p>
          <p>Reply <strong>STOP</strong> to unsubscribe at any time. Reply <strong>HELP</strong> for help. Support: shortstackllc@gmail.com.</p>
          <p>Carriers are not liable for delayed or undelivered messages. Message delivery is subject to your mobile carrier and network availability.</p>
          <p>See our <a href="/privacy" className="underline font-semibold">Privacy Policy</a> and public <a href="/sms-consent" className="underline font-semibold">SMS Notifications program page</a>.</p>
        </Section>

        <Section n={6} title="AI Features">
          <p>The Service may provide AI-assisted features such as task organization, meeting summaries, scheduling, and recommendations. AI output is provided as suggestions only and may contain errors. You are solely responsible for reviewing, verifying, and approving any AI-generated content before relying on it.</p>
        </Section>

        <Section n={7} title="Your Content & Data">
          <p>You retain ownership of the business and employee data you submit to the Service. You grant us a limited license to process this data solely to operate and provide the Service. Our handling of personal information is described in our Privacy Policy.</p>
        </Section>

        <Section n={8} title="Third-Party Services">
          <p>The Service may integrate with third-party providers (including Twilio, OpenAI, Google, Microsoft, payment processors, and cloud storage providers). Your use of those integrations may be subject to the providers' own terms and privacy practices. We are not responsible for third-party services.</p>
        </Section>

        <Section n={9} title="Service Availability">
          <p>We strive to keep the Service available and reliable but do not guarantee uninterrupted or error-free operation. We may modify, suspend, or discontinue features at any time. Scheduled and unscheduled maintenance may temporarily affect availability.</p>
        </Section>

        <Section n={10} title="Disclaimers">
          <p>The Service is provided “as is” and “as available” without warranties of any kind, whether express or implied, including fitness for a particular purpose and non-infringement, to the fullest extent permitted by law.</p>
        </Section>

        <Section n={11} title="Limitation of Liability">
          <p>To the maximum extent permitted by law, Short Stack Hawaii LLC will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, revenue, or profits, arising from your use of the Service.</p>
        </Section>

        <Section n={12} title="Termination">
          <p>We may suspend or terminate access to the Service if you violate these Terms or if necessary to protect the Service or other users. You may stop using the Service at any time. Certain provisions survive termination, including ownership, disclaimers, and limitations of liability.</p>
        </Section>

        <Section n={13} title="Changes to These Terms">
          <p>We may update these Terms from time to time. When material changes occur, we will update the Effective Date and notify users through the application when appropriate. Continued use of the Service after changes become effective constitutes acceptance of the updated Terms where permitted by law.</p>
        </Section>

        <Section n={14} title="Governing Law">
          <p>These Terms are governed by the laws of the State of Hawaii, United States, without regard to its conflict-of-law principles, except where applicable law requires otherwise.</p>
        </Section>

        <Section n={15} title="Contact Us">
          <p>If you have questions about these Terms, please contact:</p>
          <p><strong>Short Stack Hawaii LLC</strong><br />
            Email: <a className="underline" href="mailto:shortstackllc@gmail.com">shortstackllc@gmail.com</a><br />
            Website: <a className="underline" href="https://www.shortstackhawaiicrew.com">https://www.shortstackhawaiicrew.com</a></p>
        </Section>

        <div className="pt-6 border-t border-border text-sm">
          <a href="/privacy" className="underline font-bold" data-testid="terms-to-privacy">View our Privacy Policy →</a>
        </div>
      </main>
    </div>
  );
}
